import {InputText} from '@/components/commons/input-text/InputText'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {useTranslation} from '@/translations/i18n'
import {Controller, useFormContext, useWatch} from 'react-hook-form'
import {Label} from '@/components/ui/label/Label'
import {InputPhone} from '@/components/commons/input-phone/InputPhone'
import {RadioField} from '@components/commons/radio-field/RadioField.tsx'
import dayjs from 'dayjs'
import {Checkbox} from '@/components/commons/checkbox/ChekBox'
import {StyledGenerateButton} from './style'
import {InputSelectCity} from '@/components/commons/input-select-city/InputSelectCity'
import {InputSelectProvince} from '@/components/commons/input-select-province/InputSelectProvince'
import {InputSelectCountry} from '@/components/commons/input-select-country/InputSelectCountry'
import {Grid, GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {InputStatusIcon} from '@/components/ui/input-status-icon/InputStatusIcon'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {useCallback, useEffect, useMemo} from 'react'
import {useAsync} from '@/hooks/useAsync'
import {httpGetTaxCode} from '@/services/personalData.http'
import {toast} from '@/utilities/toast/toast'
import {formatDate} from '@/dayjs'
import {Country} from '@/types'
import {useGetCountries} from '@/services/useGetCountries'
import {InputDate} from '@/components/commons/input-date-legacy/InputDate'
import {InputSelect} from '@/components/commons/input-select/InputSelect'
import {AxiosError} from 'axios'
import {InputTaxCode} from '@/components/ui/form-atoms/FormAtoms'
import {maritalStatusesToLabel} from '@/utilities/helpers'
import {UserPersonalDataFormValidationSchema} from '@/features/users/types'

export const UserPersonalDataForm = () => {
    const {t} = useTranslation()

    // initial queries
    const defaultCountryQuery = useGetCountries({search: 'Italia', limit: 1}, {staleTime: Infinity})

    // methods
    const form = useFormContext<UserPersonalDataFormValidationSchema>()
    const calculatePatientTaxCode = useAsync()

    // form values
    const isBornAbroad = useWatch({name: 'isBornAbroad', control: form.control})
    const birthProvince = useWatch({name: 'birthProvince', control: form.control})
    const firstName = useWatch({name: 'firstName', control: form.control})
    const lastName = useWatch({name: 'lastName', control: form.control})
    const birthDate = useWatch({name: 'birthDate', control: form.control})
    const birthCountryId = useWatch({name: 'birthCountry.id', control: form.control})
    const birthCityId = useWatch({name: 'birthCity.id', control: form.control})
    const gender = useWatch({name: 'gender', control: form.control})
    // booleans
    const isCalculateTaxCodeDisabled = useMemo(
        () => !firstName || !lastName || !gender || !birthDate || !birthCountryId || (!isBornAbroad && !birthCityId),
        [firstName, lastName, gender, birthDate, birthCountryId, isBornAbroad, birthProvince, birthCityId]
    )

    // actions
    const setInitialCountry = useCallback((country: Country) => {
        form.setValue('birthCountry', country)
    }, [])

    const onCalculateUserTaxCode = async () => {
        try {
            const {data} = await calculatePatientTaxCode.run(
                httpGetTaxCode({
                    firstName,
                    lastName,
                    gender,
                    birthDate: formatDate(birthDate.toISOString(), 'YYYY-MM-DD'),
                    birthCountryId,
                    birthCityId: isBornAbroad ? null : birthCityId
                })
            )

            if (data.cf) {
                form.setValue('taxCode', data.cf, {shouldDirty: true})
                form.clearErrors('taxCode')
            } else {
                throw new Error(t('errors:tax_code'))
            }
        } catch (error: unknown) {
            const axiosError = error as AxiosError<{code: string}>
            if (axiosError.response?.data?.code == 'taxcode-not-found') {
                form.setError('taxCode', {message: t('errors:tax_code_not_found')})
            }

            toast.error(t('errors:tax_code'))
        }
    }

    // Init country value
    useEffect(() => {
        if (defaultCountryQuery.remappedData?.[0]) {
            setInitialCountry(defaultCountryQuery.remappedData[0])
        }
    }, [defaultCountryQuery.remappedData?.[0]])

    return (
        <Flexbox gap={4} direction="column" fullWidth>
            <Flexbox gap={2} fullWidth>
                <InputText
                    type="text"
                    label={t('commons:last_name_label')}
                    placeholder="Quattrorossi"
                    errorMessage={form.formState.errors.lastName?.message}
                    maxLength={50}
                    {...form.register('lastName', {
                        onChange: () => form.setValue('taxCode', '')
                    })}
                />
                <InputText
                    type="text"
                    label={t('commons:first_name_label')}
                    placeholder="Mario"
                    maxLength={50}
                    errorMessage={form.formState.errors.firstName?.message}
                    {...form.register('firstName', {onChange: () => form.setValue('taxCode', '')})}
                />
            </Flexbox>

            <Grid columns={3} gap={2} fullWidth>
                <GridItem>
                    <InputText
                        type="email"
                        label={t('commons:email')}
                        placeholder="esempioemail@email.it"
                        maxLength={254}
                        errorMessage={form.formState.errors.email?.message}
                        {...form.register('email')}
                    />
                </GridItem>
                <GridItem>
                    <InputText
                        type="email"
                        label={t('users:pec')}
                        placeholder="esempioemail@email.it"
                        maxLength={254}
                        isOptional
                        errorMessage={form.formState.errors.pec?.message}
                        {...form.register('pec')}
                    />
                </GridItem>
                <GridItem>
                    <Controller
                        control={form.control}
                        name="phoneNumber"
                        render={({field}) => (
                            <InputPhone
                                placeholder={t('commons:phone_placeholder')}
                                label={t('commons:mobile_phone')}
                                onCallingCountryChangeCb={() => form.setValue('phoneNumber', '')}
                                errorMessage={form.formState.errors.phoneNumber?.message}
                                fullWidth
                                {...field}
                                onChange={value => field.onChange(value)}
                            />
                        )}
                    />
                </GridItem>
            </Grid>

            <Flexbox direction="column" gap={1.5}>
                <Label>{t('commons:gender_label')}</Label>
                <Flexbox direction="column" gap={1.5}>
                    <Flexbox gap={4}>
                        <RadioField
                            value={'male'}
                            label={t('commons:gender_male')}
                            {...form.register('gender', {
                                onChange: () => {
                                    form.setValue('taxCode', '')
                                    form.trigger('gender')
                                }
                            })}
                        />
                        <RadioField
                            value={'female'}
                            label={t('commons:gender_female')}
                            {...form.register('gender', {
                                onChange: () => {
                                    form.setValue('taxCode', '')
                                    form.trigger('gender')
                                }
                            })}
                        />
                    </Flexbox>
                    <Flexbox align="center" gap={1.5}>
                        <InputStatusIcon hasError={!!form.formState.errors.gender?.message} />
                        <InputHelpText error={form.formState.errors.gender?.message} />
                    </Flexbox>
                </Flexbox>
            </Flexbox>

            <Grid columns={6} gap={2} fullWidth>
                <GridItem spanColumn={2}>
                    <Controller
                        name="birthDate"
                        control={form.control}
                        render={({field}) => (
                            <InputDate
                                label={t('commons:birth_date_label')}
                                placeholder="GG/MM/AAAA"
                                errorMessage={form.formState.errors.birthDate?.message}
                                maxDate={dayjs().toDate()}
                                numMonths={1}
                                fullWidth
                                selectedDates={field.value ? [field.value] : []}
                                onChange={value => {
                                    field.onChange(value)
                                    form.setValue('taxCode', '')
                                }}
                            />
                        )}
                    />
                </GridItem>

                {isBornAbroad ? (
                    <GridItem spanColumn={2}>
                        <Controller
                            control={form.control}
                            name="birthCountry"
                            render={({field}) => (
                                <InputSelectCountry
                                    label={t('commons:birth_country_label')}
                                    value={field.value}
                                    placeholder={t('commons:select')}
                                    errorMessage={form.formState.errors.birthCountry?.message}
                                    onChange={value => {
                                        field.onChange(value)
                                        form.setValue('taxCode', '')
                                    }}
                                />
                            )}
                        />
                    </GridItem>
                ) : (
                    <>
                        <GridItem spanColumn={2}>
                            <Controller
                                control={form.control}
                                name="birthCity"
                                render={({field}) => (
                                    <InputSelectCity
                                        label={
                                            isBornAbroad ? t('commons:birth_city_label') : t('commons:birth_town_label')
                                        }
                                        value={field.value}
                                        placeholder={t('commons:select')}
                                        errorMessage={form.formState.errors.birthCity?.message}
                                        onChange={value => {
                                            if (value?.province) {
                                                form.setValue('birthProvince', value.province)
                                                form.trigger('birthProvince')
                                            }

                                            field.onChange(value)
                                            form.trigger('birthCity')
                                            form.setValue('taxCode', '')
                                        }}
                                    />
                                )}
                            />
                        </GridItem>
                        <GridItem spanColumn={2}>
                            <Controller
                                control={form.control}
                                name="birthProvince"
                                render={({field}) => (
                                    <InputSelectProvince
                                        label={t('commons:birth_province_label')}
                                        value={field.value}
                                        placeholder={t('commons:select')}
                                        errorMessage={form.formState.errors.birthProvince?.message}
                                        onChange={value => {
                                            form.resetField('birthCity')
                                            field.onChange(value)
                                            form.setValue('taxCode', '')
                                        }}
                                    />
                                )}
                            />
                        </GridItem>
                    </>
                )}
            </Grid>

            <Checkbox
                id="isBornAbroad"
                label={t('commons:born_abroad')}
                {...form.register('isBornAbroad', {
                    onChange: value => {
                        if (value?.target.checked) {
                            form.setValue('birthCountry', null)
                        } else {
                            form.setValue('birthCountry', defaultCountryQuery.remappedData[0])
                        }
                        form.setValue('birthProvince', null)
                        form.setValue('birthCity', null)
                        form.setValue('taxCode', '')
                        form.clearErrors(['birthProvince', 'birthCity', 'birthCountry'])
                    }
                })}
            />

            <Grid columns={3} gap={4} fullWidth>
                <GridItem startColumn={1} endColumn={3} gap={2} direction="column">
                    <Flexbox direction="column" fullWidth gap={1.5}>
                        <Flexbox justify="space-between" align="center" fullWidth>
                            <Label>{t('commons:fiscal_code')}</Label>
                            <StyledGenerateButton
                                disabled={isCalculateTaxCodeDisabled}
                                variant="tertiary"
                                shape="text"
                                onClick={onCalculateUserTaxCode}
                            >
                                {t('commons:generate')}
                            </StyledGenerateButton>
                        </Flexbox>
                        <InputTaxCode
                            type="text"
                            placeholder="QTTMRA98S10M141O"
                            errorMessage={
                                !calculatePatientTaxCode.isLoading ? form.formState.errors.taxCode?.message : ''
                            }
                            {...form.register('taxCode')}
                        />
                    </Flexbox>
                </GridItem>

                <GridItem startColumn={1} endColumn={3} gap={2} direction="column">
                    <Controller
                        control={form.control}
                        name="maritalStatus"
                        render={({field}) => (
                            <InputSelect
                                label={t('commons:marital_status')}
                                value={field.value ?? undefined}
                                optionsMap={maritalStatusesToLabel}
                                onChange={field.onChange}
                                onClear={() => field.onChange('')}
                                sameWidth
                                isOptional
                            />
                        )}
                    />
                </GridItem>
            </Grid>
        </Flexbox>
    )
}
